/**
 * Occupational and Skills Information System Frontend - API Constants
 * Task 4.1: Frontend Code Optimization - Consolidation Phase
 * 
 * Shared API configuration constants to eliminate duplicate values
 */

/**
 * Common API query configurations
 * Consolidates duplicate staleTime and retry values across components
 */
export const API_CONFIG = {
  // Cache durations (in milliseconds)
  CACHE_DURATION: {
    SHORT: 1000 * 60 * 2,    // 2 minutes - for search results
    MEDIUM: 1000 * 60 * 5,   // 5 minutes - for dynamic data
    LONG: 1000 * 60 * 10,    // 10 minutes - for hierarchy data
    EXTRA_LONG: 1000 * 60 * 15, // 15 minutes - for breadcrumbs
    VERY_LONG: 1000 * 60 * 30,  // 30 minutes - for measurement fields
  },
  
  // Retry settings
  RETRY: {
    DEFAULT: 1,              // Standard retry count
    AGGRESSIVE: 3,           // For critical queries
  },
  
  // Search settings
  SEARCH: {
    DEBOUNCE_DELAY: 300,     // Debounce delay in ms
    MIN_QUERY_LENGTH: 2,     // Minimum characters for search
    DEFAULT_LIMIT: 50,       // Default search result limit
    AUTOCOMPLETE_LIMIT: 10,  // Autocomplete result limit
  },
  
  // Hierarchy settings
  HIERARCHY: {
    MAX_DEPTH_OCCUPATION: 7, // Maximum depth for occupation hierarchy
    MAX_DEPTH_GUIDE: 4,      // Maximum depth for guide hierarchy
    PAGE_SIZE: 20,           // Default pagination size
  },
} as const;

/**
 * Common tRPC query options
 * Standardized options for different types of queries
 */
export const QUERY_OPTIONS = {
  // For static/rarely changing data (hierarchy, categories)
  STATIC: {
    staleTime: API_CONFIG.CACHE_DURATION.LONG,
    retry: API_CONFIG.RETRY.DEFAULT,
  },
  
  // For dynamic data (search results, user-specific data)
  DYNAMIC: {
    staleTime: API_CONFIG.CACHE_DURATION.SHORT,
    retry: API_CONFIG.RETRY.DEFAULT,
  },
  
  // For critical data (occupation details, measurement fields)
  CRITICAL: {
    staleTime: API_CONFIG.CACHE_DURATION.VERY_LONG,
    retry: API_CONFIG.RETRY.DEFAULT,
  },
  
  // For real-time data (autocomplete, search suggestions)
  REALTIME: {
    staleTime: API_CONFIG.CACHE_DURATION.MEDIUM,
    retry: API_CONFIG.RETRY.DEFAULT,
  },
} as const;
